import React from "react";
import { Autocomplete, TextField } from "@mui/material";
import { DeliveryZone } from "../../utils/deliveryQuote";
import { selectableZones } from "../../hooks/useDeliverySelection";
import { DELIVERY_TEXTS } from "../../pages/shared/constants";

/** Wide enough to hold "San José de la Montaña" where the strip does not stretch it. */
const COMPACT_MIN_WIDTH = 190;

type Props = {
  zones: DeliveryZone[] | undefined;
  value: string;
  onChange: (id: string) => void;
  size?: "small" | "medium";
  fullWidth?: boolean;
  error?: boolean;
  helperText?: string;
};

/**
 * The municipality picker, in one place because it appears twice — under the buy block and again
 * in checkout — and the two must offer exactly the same list. Suspended municipalities are absent
 * from it; `POST /orders` refuses them anyway, and offering one only to reject it at the last
 * screen is the failure this whole feature exists to avoid.
 *
 * A combobox rather than a plain `select` (EB-554): the shop serves 53 municipalities, which is
 * long enough that scanning a menu for one is work, and typing three letters is not. Filtering
 * ignores accents, so "montana" finds "San José de la Montaña" — the alternative is a shopper
 * concluding the shop does not deliver to them.
 *
 * Clearing it is allowed and then undone: `useReconcileMunicipality` puts the default straight
 * back, because "no destination" is not a state the fee, the promise or the order can be built
 * from.
 */
const MunicipalitySelect: React.FC<Props> = ({
  zones,
  value,
  onChange,
  size = "small",
  fullWidth,
  error,
  helperText,
}) => {
  const options = selectableZones(zones);
  const selected = options.find((zone) => zone.id === value) ?? null;

  return (
    <Autocomplete
      options={options}
      value={selected}
      onChange={(_, zone) => onChange(zone?.id ?? "")}
      getOptionLabel={(zone) => zone.municipality}
      isOptionEqualToValue={(option, chosen) => option.id === chosen.id}
      size={size}
      fullWidth={fullWidth}
      // Enter picks the top match without arrowing to it first, and the list is already open by
      // the time a thumb has finished tapping the field.
      autoHighlight
      openOnFocus
      noOptionsText={DELIVERY_TEXTS.municipalityNoMatch}
      sx={fullWidth ? undefined : { minWidth: COMPACT_MIN_WIDTH }}
      ListboxProps={{ style: { maxHeight: 320 } }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={DELIVERY_TEXTS.municipalityLabel}
          placeholder={DELIVERY_TEXTS.municipalityPlaceholder}
          error={error}
          helperText={helperText}
        />
      )}
    />
  );
};

export default MunicipalitySelect;
